import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Grain } from "../components/Grain";
import { FloatingWhatsApp } from "../components/FloatingWhatsApp";
import { ProductCard } from "../components/ProductCard";
import { products, CATEGORIES } from "../data/products";
import { copy } from "../data/copy";

const ShopPage = () => {
    const { lang, t } = useLang();
    const [cat, setCat] = useState("all");

    const filtered = useMemo(() => {
        return products.filter((p) => {
            if (cat !== "all" && p.category !== cat) return false;
            return true;
        });
    }, [cat]);

    return (
        <div className="min-h-screen bg-[color:var(--kp-cream)]" data-testid="shop-page">
            <Grain />
            <Header />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-px bg-[color:var(--kp-red)]" />
                        <span
                            className={`text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 ${
                                lang === "te" ? "font-telugu-body" : ""
                            }`}
                        >
                            {t({ en: "The whole shop · " + products.length + " pouches", te: "మొత్తం షాప్ · " + products.length + " ప్యాకెట్లు" })}
                        </span>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                        className={`text-6xl md:text-8xl leading-[0.95] tracking-editorial mb-8 ${
                            lang === "en" ? "font-serif-display" : "font-telugu-display"
                        }`}
                        data-testid="shop-title"
                    >
                        {t({ en: "The whole", te: "మా" })}{" "}
                        <em className={`text-[color:var(--kp-red)] ${lang === "en" ? "italic" : "not-italic"}`}>
                            {t({ en: "shelf.", te: "షెల్ఫ్." })}
                        </em>
                    </motion.h1>

                    {/* Filters */}
                    <div className="border-y border-[color:var(--kp-ink)]/12 py-4 mb-12 flex flex-wrap gap-3 items-center" data-testid="shop-filters">
                        <FilterGroup
                            label={t({ en: "Category", te: "వర్గం" })}
                            value={cat}
                            onChange={setCat}
                            options={[
                                { v: "all", l: { en: "All", te: "అన్నీ" } },
                                { v: "veg", l: copy.products.veg },
                                { v: "nonveg", l: copy.products.nonveg },
                                { v: "gongura_veg", l: copy.products.gongura_veg },
                                { v: "gongura_nonveg", l: copy.products.gongura_nonveg },
                                { v: "sweet", l: copy.products.sweet },
                            ]}
                            testId="filter-cat"
                        />
                        <div className="ml-auto text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60">
                            {filtered.length} {t({ en: "results", te: "ఫలితాలు" })}
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="text-center py-24 text-[color:var(--kp-ink)]/60" data-testid="shop-empty">
                            {t({ en: "No pouches match those filters.", te: "ఈ ఫిల్టర్‌లకు ఏ ప్యాకెట్ లేదు." })}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 md:gap-y-24" data-testid="shop-grid">
                            {filtered.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            <FloatingWhatsApp />
        </div>
    );
};

const FilterGroup = ({ label, value, onChange, options, testId }) => {
    const { t } = useLang();
    return (
        <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-mono-wide text-[color:var(--kp-ink)]/50">
                {label}
            </span>
            <div className="flex flex-wrap gap-1">
                {options.map((o) => (
                    <button
                        key={o.v}
                        onClick={() => onChange(o.v)}
                        className={`px-3 py-1.5 rounded-full text-[11px] tracking-mono-wide border transition-colors ${
                            value === o.v
                                ? "border-[color:var(--kp-red)] bg-[color:var(--kp-red)] text-[color:var(--kp-cream)]"
                                : "border-[color:var(--kp-ink)]/20 hover:border-[color:var(--kp-ink)]/50"
                        }`}
                        data-testid={`${testId}-${o.v}`}
                    >
                        {t(o.l)}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default ShopPage;
